import { z } from 'zod';
import { MediaAttachment } from './media.js';

/**
 * Shared shapes for the standalone mail-client module — a minimal inbox
 * view on top of the connected agent's Microsoft mailbox. Headers-only
 * rows are cached in Postgres (`mail_messages`) so list/sort/paginate
 * stay fast; message bodies are fetched on demand from Graph and never
 * stored.
 *
 * Scope for slice 1: inbox list + single-message detail + manual sync.
 * Folders, threading, multi-account, send/reply, and attachments ship
 * in later slices.
 */

/* ---- Multi-account foundation ---------------------------------------- */

/**
 * 'primary' = the user's workspace Microsoft connection (the one that
 * also powers Teams / Calendar / Files). 'additional' = an extra
 * mailbox connected just for mail, with its own credential set.
 */
export const MailAccountKind = z.enum(['primary', 'additional']);
export type MailAccountKind = z.infer<typeof MailAccountKind>;

/** 'microsoft' today. Google / IMAP slot in as new values later. */
export const MailAccountProvider = z.enum(['microsoft']);
export type MailAccountProvider = z.infer<typeof MailAccountProvider>;

/**
 * The caller's relationship to the mailbox. 'owner' connected it;
 * 'delegate' was granted access by an admin (e.g. broker oversight of
 * an assistant's work mailbox). Delegation is the ONLY cross-user
 * path — plain admin role grants nothing.
 *
 * A delegate grant is READ-ONLY. A delegate can list, open, download
 * and search the mailbox; they cannot send, reply, save a draft,
 * delete, move, archive, flag or mark read. The server enforces that
 * on every /api/mail/* route (403 FORBIDDEN), so 'delegate' is the
 * signal a client uses to stop offering write affordances at all.
 * Sending from a second address is served by connecting it yourself
 * (kind='additional'), which makes you its owner.
 */
export const MailAccountRole = z.enum(['owner', 'delegate']);
export type MailAccountRole = z.infer<typeof MailAccountRole>;

export const MailAccountStatus = z.enum(['active', 'reauth_required']);
export type MailAccountStatus = z.infer<typeof MailAccountStatus>;

export const MailAccountDelegate = z.object({
  userId: z.number().int().positive(),
  name: z.string(),
  email: z.string(),
  note: z.string().nullable(),
});
export type MailAccountDelegate = z.infer<typeof MailAccountDelegate>;

export const MailAccount = z.object({
  id: z.number().int().positive(),
  kind: MailAccountKind,
  provider: MailAccountProvider,
  role: MailAccountRole,
  status: MailAccountStatus,
  accountEmail: z.string(),
  displayName: z.string().nullable(),
  /** On delegated accounts: the owning user's display name. Null on
   *  the caller's own accounts. */
  ownerName: z.string().nullable(),
  /** Who has read-only delegated access to this mailbox. Always
   *  populated — the owner MUST be able to see exactly who can read
   *  their mail. */
  delegates: z.array(MailAccountDelegate),
});
export type MailAccount = z.infer<typeof MailAccount>;

export const MailAccountsResponse = z.object({
  accounts: z.array(MailAccount),
});
export type MailAccountsResponse = z.infer<typeof MailAccountsResponse>;

/**
 * Optional mailbox selector accepted by every /api/mail/* route.
 * Omitted = the caller's primary mailbox (backward compatible with
 * pre-multi-account clients, including mobile). The server rejects
 * ids the caller neither owns nor holds a delegate grant for (404,
 * indistinguishable from "no such mailbox", so ids don't leak).
 *
 * The selector grants READING only on a mailbox reached through a
 * delegate grant. Pointing it at someone else's mailbox on a route
 * that sends, drafts, deletes, moves, archives, flags or files
 * returns 403 FORBIDDEN — see MailAccountRole.
 */
export const MailAccountScopeQuery = z.object({
  accountId: z.coerce.number().int().positive().optional(),
});
export type MailAccountScopeQuery = z.infer<typeof MailAccountScopeQuery>;

/**
 * The 403 body a write route returns when the selected mailbox is one
 * the caller only holds a delegate grant on. One string, exported, so
 * the API, the dashboard's read-only banner and the MCP tools all say
 * the same thing about the same rule.
 */
export const MAIL_DELEGATE_READ_ONLY_MESSAGE =
  'Read-only mailbox. You can read and search this mailbox, but not send, reply, or change it.';

/**
 * Admin-only: grant a user READ-ONLY delegated access to a mailbox.
 * There is no access level on the grant, by design — see
 * MailAccountRole.
 */
export const MailDelegateGrantRequest = z.object({
  delegateUserId: z.number().int().positive(),
  note: z.string().trim().max(255).optional(),
});
export type MailDelegateGrantRequest = z.input<typeof MailDelegateGrantRequest>;

export const MailAccountDisconnectResponse = z.object({
  disconnected: z.literal(true),
});
export type MailAccountDisconnectResponse = z.infer<typeof MailAccountDisconnectResponse>;

/**
 * Admin-only mailbox directory used to manage delegation grants —
 * METADATA ONLY (who owns which registered mailbox + who it's shared
 * with). It grants no message access whatsoever; reads still require
 * an explicit delegate grant, admin or not.
 */
export const MailAccountDirectoryEntry = z.object({
  id: z.number().int().positive(),
  kind: MailAccountKind,
  provider: MailAccountProvider,
  status: MailAccountStatus,
  accountEmail: z.string(),
  ownerUserId: z.number().int().positive(),
  ownerName: z.string(),
  delegates: z.array(MailAccountDelegate),
});
export type MailAccountDirectoryEntry = z.infer<typeof MailAccountDirectoryEntry>;

export const MailAccountDirectoryResponse = z.object({
  accounts: z.array(MailAccountDirectoryEntry),
});
export type MailAccountDirectoryResponse = z.infer<typeof MailAccountDirectoryResponse>;

// Keep this in sync with `ContactEmailMatchSource` in crm.ts and the
// `IsMatchedSource` type in `packages/db/src/inbound-matcher.ts`. The
// matcher will write `to_address` for outbound messages whose
// recipient resolves to a CRM contact, so the enum has to allow it
// or the /mail list endpoint serializes-fails on Sent Items.
export const MailMatchSource = z.enum([
  'header',
  'in_reply_to',
  'from_address',
  'to_address',
]);
export type MailMatchSource = z.infer<typeof MailMatchSource>;

/**
 * Folders whose messages are addressed BY the agent rather than TO them.
 *
 * Their `from` is either the connected mailbox itself (Sent Items) or
 * empty (Drafts, Scheduled, Outbox — Graph doesn't stamp a sender until
 * the message goes out). So a list row that always renders the sender
 * labels every message with the agent's own name, or with nothing at
 * all: the mobile Drafts folder read as 216 rows of "Unknown sender",
 * because it had no notion of this at all while the dashboard kept a
 * private copy of the set. One list, imported everywhere — a surface
 * that forgets it doesn't look broken to the developer, it looks broken
 * to whoever is trying to find their draft.
 */
export const RECIPIENT_DISPLAY_FOLDERS: ReadonlySet<string> = new Set([
  'sentitems',
  'drafts',
  'outbox',
  'scheduled',
]);

/** Does this folder's list render the recipient instead of the sender? */
export function isRecipientDisplayFolder(folder: string | null | undefined): boolean {
  return folder != null && RECIPIENT_DISPLAY_FOLDERS.has(folder.trim().toLowerCase());
}

export const MailMessageListItem = z.object({
  // Positive for rows cached in mail_messages; negative synthetic
  // values for rows pulled live from Graph (non-inbox folders).
  // Client uses the value as a selection key only.
  id: z.number().int(),
  graphMessageId: z.string(),
  subject: z.string().nullable(),
  fromName: z.string().nullable(),
  fromAddress: z.string().nullable(),
  /** Recipient addresses on the message — populated for every row so
   *  Sent Items / Drafts can render "To: ..." instead of always showing
   *  the connected agent as the sender. Empty array on inbound rows
   *  that predate this column. */
  toAddresses: z.array(z.string()).default([]),
  bodyPreview: z.string().nullable(),
  receivedAt: z.string().datetime({ offset: true }).nullable(),
  isRead: z.boolean(),
  /** Follow-up flag state, mirrored from Graph. */
  flagged: z.boolean(),
  /** Graph's `lastVerbExecuted` normalized — null = no response yet,
   *  or one of "reply" / "replyAll" / "forward". Surfaced on inbox
   *  rows so the agent sees they've already responded to a thread
   *  without opening it. */
  lastVerb: z.string().nullable(),
  /** Mirrors Graph's `message.hasAttachments`. Used by the inbox
   *  "has attachments" filter. */
  hasAttachments: z.boolean(),
  /** Outlook category names assigned to the message — Graph's
   *  `message.categories` round-tripped verbatim. Known names map
   *  to the standard Outlook palette ("Red category", "Orange
   *  category", "Yellow category", "Green category", "Blue category",
   *  "Purple category"); unknown names are user-defined and render
   *  with a neutral chip. Older clients defaulted to []. */
  categories: z.array(z.string()).default([]),
  // When the inbound matcher attributed this message to a CRM contact,
  // slice 3 surfaces the link + how we matched. Null = unmatched.
  matchedContactId: z.number().int().positive().nullable(),
  matchSource: MailMatchSource.nullable(),
  // Display name of the matched contact, joined in at list time so the
  // `/mail` UI can render a link without a second roundtrip. Null when
  // unmatched or the contact has since been deleted.
  matchedContactName: z.string().nullable(),
});
export type MailMessageListItem = z.infer<typeof MailMessageListItem>;

/**
 * Standard Outlook category names that map to the six preset color
 * chips on every Outlook mailbox. The mail UI uses these to render
 * colored category chips on rows + the detail header. Custom user-
 * defined categories pass through as plain text strings.
 */
export const MAIL_PRESET_CATEGORIES = [
  'Red category',
  'Orange category',
  'Yellow category',
  'Green category',
  'Blue category',
  'Purple category',
] as const;
export type MailPresetCategory = (typeof MAIL_PRESET_CATEGORIES)[number];

export const MailMessagesResponse = z.object({
  messages: z.array(MailMessageListItem),
  total: z.number().int().nonnegative(),
  // Timestamp of the most-recently-synced cached message, so the UI
  // can render "last synced 2 min ago". Null when no messages are
  // cached yet. The worker background job updates this implicitly by
  // setting mail_messages.synced_at on every upsert.
  lastSyncedAt: z.string().datetime({ offset: true }).nullable(),
});
export type MailMessagesResponse = z.infer<typeof MailMessagesResponse>;

/**
 * One attachment on a message, as the UI needs it: a name, a size, a type.
 *
 * METADATA ONLY, DELIBERATELY. Graph returns `contentBytes` for every
 * `fileAttachment` on an unfiltered `/attachments` request, so listing a
 * draft carrying a 6 MB PDF would drag ~8 MB of base64 across the wire just
 * to draw a filename chip. Every server-side read of this shape goes through
 * `listMessageAttachments`, which pins `$select` to these fields. Do not add
 * a bytes field here — downloading is a separate, explicit endpoint.
 */
export const MailMessageAttachment = z.object({
  /** Graph's attachment id on the message. Null when the attachment is
   *  only tracked locally — i.e. we hold the bytes but the push onto the
   *  Graph message hasn't landed yet. */
  attachmentId: z.string().nullable(),
  /** `media_attachments` id when the file is also stored locally. Null for
   *  an attachment that only ever existed on the Graph message (anything
   *  added by `mail_attach_drive_item` / `mail_attach_file`). */
  mediaAttachmentId: z.number().int().positive().nullable(),
  name: z.string(),
  contentType: z.string().nullable(),
  size: z.number().int().nonnegative(),
});
export type MailMessageAttachment = z.infer<typeof MailMessageAttachment>;

export const MailMessageDetail = z.object({
  id: z.number().int().positive(),
  graphMessageId: z.string(),
  subject: z.string().nullable(),
  fromName: z.string().nullable(),
  fromAddress: z.string().nullable(),
  toAddresses: z.array(z.string()),
  /** Cc / Bcc on the message — resolved live from Graph for every
   *  message so the reading pane shows the full recipient set (a
   *  sender cc'ing a second party was previously invisible in our
   *  UI), and drafts can resume every recipient slot. Bcc is only
   *  present on sent/draft mail the agent owns. Older clients ignored
   *  these fields, so they're optional with empty-array defaults. */
  ccAddresses: z.array(z.string()).default([]),
  bccAddresses: z.array(z.string()).default([]),
  receivedAt: z.string().datetime({ offset: true }).nullable(),
  isRead: z.boolean(),
  flagged: z.boolean(),
  /** Outlook category names assigned to the message — same shape as
   *  the list-item `categories` field. Older clients defaulted to []. */
  categories: z.array(z.string()).default([]),
  /** True when the message is currently a draft in Outlook. Powers
   *  the "Edit draft" UI on the message pane — drafts get a resume
   *  button instead of Reply / Reply All / Forward. */
  isDraft: z.boolean().default(false),
  /** Mirrors Graph's `message.hasAttachments`. */
  hasAttachments: z.boolean().default(false),
  /** Every non-inline attachment actually on the message, resolved from
   *  Graph (metadata only — see `MailMessageAttachment`) and merged with
   *  anything we track locally. This is what the composer and the contact
   *  timeline render; `hasAttachments` alone can't tell them the names. */
  attachments: z.array(MailMessageAttachment).default([]),
  webLink: z.string().nullable(),
  // Body comes straight from Graph — HTML if available, else text.
  bodyContentType: z.enum(['html', 'text']).nullable(),
  bodyContent: z.string().nullable(),
});
export type MailMessageDetail = z.infer<typeof MailMessageDetail>;

export const MailMessageDetailResponse = z.object({ message: MailMessageDetail });
export type MailMessageDetailResponse = z.infer<typeof MailMessageDetailResponse>;

/**
 * Inbound-attachment summary on a single message. Populated from
 * Graph's `/me/messages/{id}/attachments` endpoint on demand — we
 * don't cache attachment metadata in `mail_messages` since it's
 * rarely the thing Kenneth reaches for.
 */
export const MailAttachmentSummary = z.object({
  id: z.string(),
  name: z.string(),
  contentType: z.string().nullable(),
  size: z.number().int().nonnegative(),
  isInline: z.boolean(),
});
export type MailAttachmentSummary = z.infer<typeof MailAttachmentSummary>;

export const MailAttachmentListResponse = z.object({
  attachments: z.array(MailAttachmentSummary),
});
export type MailAttachmentListResponse = z.infer<typeof MailAttachmentListResponse>;

export const MailAttachmentDownloadParams = z.object({
  id: z.coerce.number().int().positive(),
  attachmentId: z.string().min(1).max(512),
});
export type MailAttachmentDownloadParams = z.infer<typeof MailAttachmentDownloadParams>;

export const MailAttachmentDownloadQuery = z.object({
  inline: z.coerce.boolean().optional(),
  accountId: z.coerce.number().int().positive().optional(),
});
export type MailAttachmentDownloadQuery = z.infer<typeof MailAttachmentDownloadQuery>;

/**
 * Copies downloadable file attachments from an Outlook draft into
 * YRP media rows so the normal composer attachment chips and
 * outbound-send worker can reuse them without asking the agent to
 * upload the same file again.
 */
export const MailDraftAttachmentImportResponse = z.object({
  attachments: z.array(MediaAttachment),
});
export type MailDraftAttachmentImportResponse = z.infer<typeof MailDraftAttachmentImportResponse>;

/**
 * Top-level mail folders for the connected mailbox. `wellKnownName`
 * is Graph's canonical name (Inbox / SentItems / Drafts / …) and is
 * null for user-created folders. `unread` is the server-side unread
 * count — Graph keeps this current even when the inbox hasn't been
 * synced to our cache table yet.
 */
export const MailFolder = z.object({
  id: z.string(),
  displayName: z.string(),
  wellKnownName: z.string().nullable(),
  unread: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});
export type MailFolder = z.infer<typeof MailFolder>;

export const MailFoldersResponse = z.object({
  folders: z.array(MailFolder),
});
export type MailFoldersResponse = z.infer<typeof MailFoldersResponse>;

/**
 * Ad-hoc compose / reply / forward from the /mail page. Unlike the
 * CRM send-email endpoint, this one doesn't bind the outbound message
 * to a specific contact — the recipient may or may not be in the
 * CRM. If they happen to be, the existing inbound matcher will pick
 * up the reply just like any other inbound.
 */
/**
 * How many uploaded attachments one outgoing message may carry.
 *
 * Graph itself has no count limit (only the 150MB total-size ceiling
 * enforced in the worker), but every layer that touches a compose has to
 * agree on ONE number: the send request, the save-draft request, the CRM
 * composer, the attachment bar the agent clicks, and the workspace
 * snapshot the dashboard persists across tab switches. When they
 * disagree, the UI accepts files that a later layer rejects — which is
 * exactly how attaching an 11th file used to take down the whole Mail
 * module (the composer allowed it, the snapshot schema refused it, and
 * the refusal surfaced as a route crash). Import this; never restate it.
 */
export const MAIL_ATTACHMENT_COUNT_LIMIT = 10;

export const MailComposeRequest = z.object({
  to: z.array(z.string().trim().email()).min(1).max(25),
  cc: z.array(z.string().trim().email()).max(25).optional(),
  bcc: z.array(z.string().trim().email()).max(25).optional(),
  subject: z.string().trim().max(255),
  // 1 MB — matches Fastify's default bodyLimit. The previous 100 KB
  // cap rejected normal-looking replies once the quoted thread (full
  // HTML, recursively quoted) plus the user's body exceeded it.
  body: z.string().max(1_000_000),
  /**
   * IDs of `media_attachments` rows the caller previously uploaded via
   * `/api/media/upload`. Server resolves each id, reads the bytes off
   * the storage adapter, base64-encodes, and passes as Graph inline
   * file attachments. Capped at `MAIL_ATTACHMENT_COUNT_LIMIT` per
   * message + a total-size cap enforced server-side.
   */
  attachmentIds: z
    .array(z.number().int().positive())
    .max(MAIL_ATTACHMENT_COUNT_LIMIT)
    .optional(),
  /**
   * Local `mail_messages` id of the Outlook draft this send is resuming.
   *
   * When present the server sends THAT draft in place (PATCH the fields
   * above onto it, then POST /send) instead of composing a new message.
   * That is the only way attachments already sitting on the draft — ones
   * the composer may never have known about, because they were pushed
   * straight onto the Graph message — actually go out with it. Omit for a
   * from-scratch compose.
   */
  draftMessageId: z.number().int().positive().optional(),
});
export type MailComposeRequest = z.input<typeof MailComposeRequest>;

/**
 * Reply from POST /mail/compose. The send isn't immediate any more —
 * the API queues the message for a 30-second undo window, then a
 * worker picks it up. Caller polls or simply waits; the undo-send
 * toast UI uses `outboxId` to call DELETE /mail/outbox/:id within the
 * window. `scheduledFor` is the ISO timestamp the message will fire
 * (now + delaySeconds).
 */
export const MailComposeResponse = z.object({
  outboxId: z.number().int().positive(),
  scheduledFor: z.string(),
  delaySeconds: z.number().int().nonnegative(),
});
export type MailComposeResponse = z.infer<typeof MailComposeResponse>;

/**
 * Save the standalone /mail composer into Outlook Drafts. Unlike
 * send, drafts can be incomplete: a subject/body may still be blank
 * and recipients are allowed to be empty while the agent is mid-reply.
 * When `draftMessageId` is present, the API updates that cached
 * Outlook draft in place; otherwise it creates a new Outlook draft and
 * immediately mirrors it into the local Drafts cache.
 */
export const MailDraftSaveRequest = z.object({
  draftMessageId: z.number().int().positive().optional(),
  /**
   * When the draft is composed from a CRM contact context (reply /
   * forward off a contact's email), the contact id is threaded through
   * so the save ALSO records a `crm_contact_email_drafts` pointer and the
   * draft surfaces on that contact's timeline. Omitted for standalone
   * /mail composes, which stay Outlook-only.
   */
  contactId: z.number().int().positive().optional(),
  to: z.array(z.string().trim().email()).max(25).default([]),
  cc: z.array(z.string().trim().email()).max(25).default([]),
  bcc: z.array(z.string().trim().email()).max(25).default([]),
  subject: z.string().trim().max(255).default(''),
  body: z.string().max(1_000_000).default(''),
  attachmentIds: z
    .array(z.number().int().positive())
    .max(MAIL_ATTACHMENT_COUNT_LIMIT)
    .optional(),
});
export type MailDraftSaveRequest = z.input<typeof MailDraftSaveRequest>;

export const MailDraftSaveResponse = z.object({
  id: z.number().int().positive(),
  graphMessageId: z.string(),
  updatedAt: z.string().datetime({ offset: true }),
});
export type MailDraftSaveResponse = z.infer<typeof MailDraftSaveResponse>;

/**
 * Attach an existing OneDrive/SharePoint drive item to an existing
 * Outlook DRAFT, entirely server-side (POST
 * /mail/drafts/:draftId/attach-drive-item). The whole point is that file
 * bytes never transit the API caller / model: the server downloads the
 * drive item and re-attaches it to the draft over Microsoft Graph. So
 * this request carries only ids, and the response carries only metadata
 * — there is intentionally NO `contentBytes` field anywhere here.
 *
 * Exactly one of `itemId` (single) or `itemIds` (batch, continue-on-
 * error) must be supplied. `driveId` selects a SharePoint document
 * library; omit it for the user's personal OneDrive. `fileName`
 * overrides the attachment's display name and only applies to the
 * single-item form.
 */
export const MailDraftAttachDriveItemRequest = z
  .object({
    itemId: z.string().trim().min(1).max(512).optional(),
    itemIds: z.array(z.string().trim().min(1).max(512)).min(1).max(25).optional(),
    driveId: z.string().trim().min(1).max(512).optional(),
    fileName: z.string().trim().min(1).max(512).optional(),
  })
  .refine((d) => (d.itemId != null) !== (d.itemIds != null && d.itemIds.length > 0), {
    message: 'Provide exactly one of itemId or itemIds',
  });
export type MailDraftAttachDriveItemRequest = z.infer<typeof MailDraftAttachDriveItemRequest>;

/**
 * Per-item outcome. `attached` = newly attached; `skipped` = a file
 * attachment with the same display name was already on the draft, so the
 * existing attachment is returned unchanged (idempotent re-runs); `error`
 * = this item failed (batch mode continues past it — single mode surfaces
 * an HTTP error instead). `message` explains a skip/error.
 *
 * Dedup is keyed on display name, NOT byte size — Graph's stored
 * attachment size includes encoding overhead and never equals the source
 * file's size. Tradeoff: two distinct files sharing a display name won't
 * both attach; pass a distinct `fileName` to force the second.
 */
export const MailDraftAttachDriveItemResult = z.object({
  itemId: z.string(),
  status: z.enum(['attached', 'skipped', 'error']),
  attachmentId: z.string().optional(),
  name: z.string().optional(),
  size: z.number().int().nonnegative().optional(),
  contentType: z.string().nullable().optional(),
  message: z.string().optional(),
});
export type MailDraftAttachDriveItemResult = z.infer<typeof MailDraftAttachDriveItemResult>;

export const MailDraftAttachDriveItemResponse = z.object({
  /** The local YRP draft id the items were attached to. */
  draftMessageId: z.number().int().positive(),
  results: z.array(MailDraftAttachDriveItemResult),
});
export type MailDraftAttachDriveItemResponse = z.infer<typeof MailDraftAttachDriveItemResponse>;

/**
 * Attach caller-supplied bytes directly to an existing Outlook DRAFT
 * (POST /mail/drafts/:draftId/attachments). Complements
 * attach-drive-item: use this when the file was just generated and does
 * NOT live in OneDrive/SharePoint (offer summaries, GFEs, net sheets) —
 * no drive round-trip. Never sends; the draft stays a draft. The 20 MB
 * binary ceiling mirrors /files/upload-base64 (base64 inflates ~4/3,
 * hence the ~28M char cap).
 */
export const MailDraftAttachFileRequest = z
  .object({
    /** Display name. Required with contentBase64; defaults to the stored name with mediaId. */
    fileName: z.string().trim().min(1).max(255).optional(),
    /** Base64-encoded file bytes (standard alphabet, padding optional). */
    contentBase64: z.string().min(1).max(28_000_000).optional(),
    /**
     * Or a file already in YRP storage that the caller uploaded, e.g. one
     * staged with POST /media/upload-url. The server reads it; no bytes in
     * the request. This is how a staged file goes onto an EXISTING draft:
     * POST /mail/drafts with draftMessageId would rewrite the whole draft.
     */
    mediaId: z.number().int().positive().optional(),
    /** MIME type recorded on the attachment. Defaults to the stored type, else application/octet-stream. */
    contentType: z.string().trim().min(1).max(255).optional(),
  })
  .refine((v) => (v.contentBase64 !== undefined) !== (v.mediaId !== undefined), {
    message: 'supply exactly one of contentBase64 or mediaId',
  })
  .refine((v) => v.contentBase64 === undefined || v.fileName !== undefined, {
    message: 'fileName is required with contentBase64',
  });
export type MailDraftAttachFileRequest = z.infer<typeof MailDraftAttachFileRequest>;

/**
 * `skipped` = a file attachment with the same display name was already
 * on the draft (idempotent re-runs return it unchanged) — same
 * name-keyed dedup as attach-drive-item.
 */
export const MailDraftAttachFileResponse = z.object({
  /** The local YRP draft id the file was attached to. */
  draftMessageId: z.number().int().positive(),
  status: z.enum(['attached', 'skipped']),
  attachmentId: z.string(),
  name: z.string(),
  size: z.number().int().nonnegative().optional(),
  contentType: z.string().nullable().optional(),
  message: z.string().optional(),
});
export type MailDraftAttachFileResponse = z.infer<typeof MailDraftAttachFileResponse>;

/**
 * Create a REPLY draft to a message in the caller's mailbox
 * (POST /mail/messages/:id/reply-draft): same conversation, "RE:" subject,
 * the original quoted underneath, exactly what Outlook's Reply / Reply all
 * makes. `POST /mail/drafts` cannot thread; it starts a new conversation.
 *
 * Never sends. The new draft is cached like any other, so its local id works
 * with the attach routes and with `POST /mail/drafts` (draftMessageId) for
 * later edits.
 */
export const MailReplyDraftRequest = z.object({
  /** Reply to everyone on the original, not just the sender. */
  replyAll: z.boolean().default(false),
  /** The new text, above the quoted original. HTML; plain text has its line breaks kept. */
  body: z.string().min(1).max(1_000_000),
  /** Replace the To line Outlook derived from the original. Omit to keep it. */
  to: z.array(z.string().trim().email()).min(1).max(25).optional(),
  /** Replace the Cc line (send [] to clear it). Omit to keep what reply/reply-all set. */
  cc: z.array(z.string().trim().email()).max(25).optional(),
  bcc: z.array(z.string().trim().email()).max(25).optional(),
  /** The caller's own uploads (media ids) to attach, e.g. a file staged with POST /media/upload-url. */
  attachmentIds: z.array(z.number().int().positive()).max(MAIL_ATTACHMENT_COUNT_LIMIT).optional(),
  /** Surface the draft on this CRM contact's timeline, as POST /mail/drafts does. */
  contactId: z.number().int().positive().optional(),
});
export type MailReplyDraftRequest = z.input<typeof MailReplyDraftRequest>;

export const MailReplyDraftResponse = z.object({
  /** Local draft id: what the attach routes and POST /mail/drafts take. */
  id: z.number().int().positive(),
  graphMessageId: z.string(),
  /** The local id of the message replied to. */
  inReplyToMessageId: z.number().int().positive(),
  subject: z.string().nullable(),
  to: z.array(z.string()),
  cc: z.array(z.string()),
  updatedAt: z.string().datetime({ offset: true }),
});
export type MailReplyDraftResponse = z.infer<typeof MailReplyDraftResponse>;

/**
 * Copy an attachment from a message onto a DRAFT in the same mailbox
 * (POST /mail/drafts/:draftId/attach-from-message), server-side, so the
 * bytes never pass through the caller. Same name-keyed dedupe and response
 * as the other attach routes.
 */
export const MailDraftAttachFromMessageRequest = z.object({
  /** Local id of the message carrying the attachment. Must be in the draft's mailbox. */
  messageId: z.number().int().positive(),
  attachmentId: z.string().trim().min(1).max(512),
  /** Override the attachment's display name on the draft. */
  fileName: z.string().trim().min(1).max(255).optional(),
});
export type MailDraftAttachFromMessageRequest = z.infer<typeof MailDraftAttachFromMessageRequest>;

/**
 * Reply from POST /mail/outbox/:id/cancel. Cancels a queued send if
 * still in the pending window. 404 if id unknown, 409 if already sent.
 */
export const MailOutboxCancelResponse = z.object({
  ok: z.literal(true),
  outboxId: z.number().int().positive(),
});
export type MailOutboxCancelResponse = z.infer<typeof MailOutboxCancelResponse>;

/**
 * Server-side mailbox search results — Graph `$search` over the user's
 * full mail history (subject / body / sender / recipients / attachment
 * file names). Returned rows aren't cached locally; they're shown
 * inline as "matches across mailbox" when the agent's local-cache
 * search comes up empty or short.
 */
/**
 * Mailbox search is DATE-WINDOWED, not a single ranked list, because
 * Microsoft will not sort a message search ("Don't support sort for
 * Message") and the older `/me/messages?$search=` endpoint truncates by
 * relevance at a fixed per-mailbox ceiling. Constraining each query to a
 * date range and walking ranges newest-first is what makes "most recent
 * first" true rather than incidental.
 *
 * `nextBefore` is the load-older cursor: pass it back as `before` to get
 * the next window down. Null means the walk reached the end of the
 * mailbox and there is genuinely nothing older to fetch.
 */
export const MailSearchResponse = z.object({
  query: z.string(),
  /** Newest-first list of search hits within the searched window. */
  messages: z.array(MailMessageListItem),
  /**
   * Exchange's real match count for the window searched — not the number
   * of rows returned. Lets the UI say "showing 50 of 394" honestly.
   */
  total: z.number().int().nonnegative(),
  /** Oldest instant covered by this response. Null = back to the beginning. */
  searchedSince: z.string().nullable(),
  /** Cursor for the next older page; null when history is exhausted. */
  nextBefore: z.string().nullable(),
});
export type MailSearchResponse = z.infer<typeof MailSearchResponse>;

/**
 * One ordered bulk operation against Microsoft Graph. Graph enforces a
 * low per-mailbox concurrency ceiling, so the API receives the full
 * selection in one request and processes it without client-side fan-out.
 * Graph message ids are the stable selection key for both cached rows and
 * live full-mailbox search results.
 */
export const MailBulkAction = z.enum(['read', 'archive', 'delete']);
export type MailBulkAction = z.infer<typeof MailBulkAction>;

export const MailBulkActionRequest = z.object({
  action: MailBulkAction,
  graphMessageIds: z.array(z.string().trim().min(1).max(256)).min(1).max(1000),
});
export type MailBulkActionRequest = z.input<typeof MailBulkActionRequest>;

export const MailBulkActionFailure = z.object({
  graphMessageId: z.string(),
  message: z.string(),
});
export type MailBulkActionFailure = z.infer<typeof MailBulkActionFailure>;

export const MailBulkActionResponse = z.object({
  action: MailBulkAction,
  succeeded: z.array(z.string()),
  failed: z.array(MailBulkActionFailure),
});
export type MailBulkActionResponse = z.infer<typeof MailBulkActionResponse>;

export const MailSearchCacheRequest = z.object({
  graphMessageId: z.string().trim().min(1).max(256),
});
export type MailSearchCacheRequest = z.input<typeof MailSearchCacheRequest>;

export const MailSearchCacheResponse = z.object({
  message: MailMessageListItem,
});
export type MailSearchCacheResponse = z.infer<typeof MailSearchCacheResponse>;

/**
 * Trigger a fresh historical backfill for the connected user. Resets
 * the `mail_backfill_at` flag so the worker re-walks the mailbox on
 * its next pass; useful after extending the backfill window
 * (e.g. 6 months → 12 months) or when an agent reports missing mail.
 */
export const MailBackfillResponse = z.object({
  triggered: z.boolean(),
});
export type MailBackfillResponse = z.infer<typeof MailBackfillResponse>;

/**
 * Replace the set of Outlook categories on a single message. Sent in
 * one atomic PATCH — Graph's `categories` field is an array, not a
 * delta, so the server overwrites with whatever the caller provides.
 * Each name is capped at 64 chars (Outlook's own limit) and the array
 * at 25 entries (well above any sensible per-message tagging).
 */
export const MailSetCategoriesRequest = z.object({
  categories: z.array(z.string().trim().min(1).max(64)).max(25),
});
export type MailSetCategoriesRequest = z.input<typeof MailSetCategoriesRequest>;

export const MailSyncResponse = z.object({
  fetched: z.number().int().nonnegative(),
  inserted: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  // How many inbound messages the slice-3 contact matcher attributed
  // to a CRM contact this tick.
  matched: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});
export type MailSyncResponse = z.infer<typeof MailSyncResponse>;
